/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { AuthProvider, AppProvider, GameProvider, useAuth, useApp, useGame } from "./contexts";
import { isFirebaseConfigured } from "./utils/firebase";
import GoogleAuthModal from "./components/auth/GoogleAuthModal";
import AppDownloadModal from "./components/app/AppDownloadModal";
import { AppSidebar } from "./components/layout/AppSidebar";
import { AppHeader } from "./components/layout/AppHeader";
import { IncompleteProfileBanner } from "./components/profile/IncompleteProfileBanner";
import { PushNotificationActivationBanner } from "./components/notifications/PushNotificationActivationBanner";
import { AppRoutes } from "./routes/AppRoutes";
import { motion, AnimatePresence } from "motion/react";
import { Mail } from "lucide-react";
import HubEsportivoLandingPage from "./components/public/HubEsportivoLandingPage";
import { ROUTES } from "./routes/routeConfig";

function FullScreenLoader({ theme }: { theme: string }) {
  return (
    <div className={`fixed inset-0 flex flex-col items-center justify-center min-h-screen transition-colors duration-300 ${
      theme === "light" ? "bg-slate-50 text-slate-900" : "bg-zinc-950 text-zinc-100"
    }`}>
      <div className="w-10 h-10 border-4 border-amber-400/20 border-t-amber-400 rounded-full animate-spin mb-4" />
      <p className="text-xs font-bold text-zinc-400 tracking-wider uppercase animate-pulse">
        Carregando...
      </p>
    </div>
  );
}

function AppContent() {
  const {
    user,
    userProfile,
    isLoggedIn,
    isAuthReady,
    showGoogleAuthModal,
    setShowGoogleAuthModal,
    openAuthModalWithRole,
    setUserProfile,
    handleGoogleLogin,
    handleGoogleLogout,
  } = useAuth();

  const {
    theme,
    setTheme,
    zoomLevel,
    handleZoomIn,
    handleZoomOut,
    handleResetZoom,
    activeTab,
    handleSelectTab,
    isSidebarOpen,
    setIsSidebarOpen,
    isAdminMode,
    isSuperAdminAuth,
    setIsSuperAdminAuth,
    adminSubTab,
    setAdminSubTab,
    showDownloadAppModal,
    setShowDownloadAppModal,
    toastMsg,
    showToast,
    rachaHostId,
    handleExitCoOrganizing,
  } = useApp();

  const {
    duplas,
    globalDuplas,
    gameState,
    setGameState,
    historico,
    torneios,
    sessoes,
    matchSeconds,
    isTimerRunning,
    formatTime,
    getEffectiveCloudUid,
    handleAddDupla,
    handleEditDupla,
    handleDeleteDupla,
    handleDuplicateDupla,
    handleApplySorteio,
    handleStartRachao,
    handleEndRachao,
    handleReactivateSession,
    handleDeleteSession,
    handleClearAllSessions,
    handleConfirmRound,
    handleUpdateQueue,
    handleUpdateDuplas,
    handleUpdateGameState,
    handleSaveTorneio,
    handleDeleteTorneio,
    handleImportJSON,
    handleClearHistory,
    handleResetAllData,
    exportJSON,
    exportExcel,
  } = useGame();

  const location = useLocation();
  const navigate = useNavigate();
  const isLandingRoute = location.pathname === "/" || location.pathname === "";

  if (!isAuthReady) {
    return <FullScreenLoader theme={theme} />;
  }

  if (isLandingRoute && isLoggedIn) {
    return <Navigate to={ROUTES.HUB} replace />;
  }

  if (isLandingRoute) {
    return (
      <div
        className={`min-h-screen flex flex-col font-sans transition-colors duration-300 ${
          theme === "light"
            ? "bg-slate-50 text-slate-900"
            : "bg-zinc-950 text-zinc-100"
        }`}
      >
        <HubEsportivoLandingPage
          onOpenGoogleAuth={(role) => openAuthModalWithRole(role)}
          onGoToHub={() => {
            handleSelectTab("hub");
            navigate(ROUTES.HUB);
          }}
          isLoggedIn={isLoggedIn}
          userProfile={userProfile}
          theme={theme}
          setTheme={setTheme}
        />

        {/* MODAL DE AUTENTICAÇÃO COM GOOGLE REUTILIZADO */}
        <GoogleAuthModal
          user={user}
          userProfile={userProfile}
          isOpen={showGoogleAuthModal}
          onClose={() => setShowGoogleAuthModal(false)}
          onUpdateProfile={(updated) => {
            setUserProfile(updated);
          }}
          onLoginSuccess={() => {
            handleSelectTab("hub");
            navigate(ROUTES.HUB);
          }}
          onRedirectToProfile={() => {
            handleSelectTab("perfil");
            navigate("/perfil");
          }}
          onToast={showToast}
        />

        {/* MODAL DE DOWNLOAD DO APP */}
        <AppDownloadModal
          isOpen={showDownloadAppModal}
          onClose={() => setShowDownloadAppModal(false)}
          onToast={showToast}
        />
      </div>
    );
  }

  return (
    <div
      style={{ zoom: `${zoomLevel}%` }}
      className={`min-h-screen flex font-sans selection:bg-amber-400 selection:text-emerald-950 transition-colors duration-300 ${
        theme === "light"
          ? "bg-slate-50 text-slate-900"
          : "bg-zinc-950 text-zinc-100"
      }`}
    >
      {/* MOBILE/DESKTOP UNIFIED SIDEBAR DRAWER */}
      <AppSidebar
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        activeTab={activeTab}
        setActiveTab={handleSelectTab}
        adminSubTab={adminSubTab}
        setAdminSubTab={setAdminSubTab}
        isAdminMode={isAdminMode}
        user={user}
        userProfile={userProfile}
        handleGoogleLogin={handleGoogleLogin}
        onOpenAuthModal={() => setShowGoogleAuthModal(true)}
        handleGoogleLogout={handleGoogleLogout}
        zoomLevel={zoomLevel}
        handleZoomIn={handleZoomIn}
        handleZoomOut={handleZoomOut}
        handleResetZoom={handleResetZoom}
        handleResetAllData={handleResetAllData}
        handleImportJSON={handleImportJSON}
        isFirebaseConfigured={isFirebaseConfigured}
        theme={theme}
        setTheme={setTheme}
      />

      {/* MAIN CONTENT AREA */}
      <div className="flex flex-col flex-grow min-h-screen w-full overflow-x-hidden">
        {/* UNIFIED TOP-BAR HEADER (BOTH PC AND MOBILE) */}
        <AppHeader
          activeTab={activeTab}
          setActiveTab={handleSelectTab}
          setIsSidebarOpen={setIsSidebarOpen}
          adminSubTab={adminSubTab}
          setAdminSubTab={setAdminSubTab}
          userProfile={userProfile}
          isAdminMode={isAdminMode}
          gameState={gameState}
          isTimerRunning={isTimerRunning}
          matchSeconds={matchSeconds}
          formatTime={formatTime}
          setShowDownloadAppModal={setShowDownloadAppModal}
          zoomLevel={zoomLevel}
          handleZoomIn={handleZoomIn}
          handleZoomOut={handleZoomOut}
          handleResetZoom={handleResetZoom}
          theme={theme}
          setTheme={setTheme}
        />

        {/* BANNER DISCRETO PARA ATIVAÇÃO DE PUSH NOTIFICATIONS FCM (N4-B) */}
        <PushNotificationActivationBanner />

        {/* BANNER PERSISTENTE DE PERFIL INCOMPLETO */}
        {activeTab !== "perfil" && (
          <IncompleteProfileBanner
            userProfile={userProfile}
            onCompleteProfile={() => handleSelectTab("perfil")}
          />
        )}

        {/* TOAST DE SUCESSO / ERRO */}
        <AnimatePresence>
          {toastMsg && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="fixed top-20 left-1/2 -translate-x-1/2 z-50 text-center w-[90%] max-w-sm"
            >
              <div
                className={`px-5 py-3 rounded-2xl shadow-2xl border flex items-center gap-3 text-sm font-semibold justify-center ${
                  toastMsg.type === "success"
                    ? "bg-emerald-900 border-emerald-500 text-white"
                    : "bg-rose-950 border-rose-700 text-rose-100"
                }`}
              >
                <span className="text-base">{toastMsg.type === "success" ? "✅" : "⚠️"}</span>
                <span>{toastMsg.text}</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* CONTEÚDO PRINCIPAL */}
        <main className="flex-grow w-full px-4 sm:px-6 py-8">
          <AppRoutes />
        </main>

        {/* FOOTER GLOBAL OFICIAL DO HUB ESPORTIVO (COMPACTO) */}
        <footer
          className={`border-t mt-8 py-5 px-4 sm:px-6 transition-colors text-xs ${
            theme === "light"
              ? "border-slate-200 bg-slate-50/80 text-slate-600"
              : "border-zinc-800 bg-zinc-950/80 text-zinc-400"
          }`}
        >
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-3 text-center md:text-left">
            {/* Bloco Institucional e Módulos */}
            <div className="space-y-1 max-w-3xl">
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-x-2 gap-y-0.5 text-[11px] font-medium">
                <span className={`font-bold ${theme === "light" ? "text-slate-900" : "text-slate-100"}`}>
                  Hub Esportivo
                </span>
                <span className="opacity-40">•</span>
                <span className={theme === "light" ? "text-slate-700" : "text-slate-300"}>
                  Plataforma de gestão para arenas e espaços esportivos.
                </span>
              </div>
              <p className="text-[11px] leading-relaxed opacity-85">
                Gestão de quadras, reservas, aulas, treinos, torneios, vendas, PDV e operação esportiva em uma única plataforma. Para arenas, clubes e espaços esportivos que trabalham com quadras, reservas e atividades esportivas.
              </p>
            </div>

            {/* Contato e Copyright */}
            <div className="flex flex-col sm:flex-row items-center justify-center md:justify-end gap-x-4 gap-y-1.5 shrink-0 text-[11px]">
              <a
                href="mailto:contato@hubesportivo.net.br"
                className="text-amber-600 dark:text-amber-400 hover:underline transition-colors font-semibold flex items-center gap-1.5"
              >
                <Mail size={12} />
                <span>contato@hubesportivo.net.br</span>
              </a>
              <span className="hidden sm:inline opacity-40">•</span>
              <span className="opacity-75">
                © {new Date().getFullYear()} Hub Esportivo. Todos os direitos reservados.
              </span>
            </div>
          </div>
        </footer>

        {/* MODAL DE AUTENTICAÇÃO COM GOOGLE */}
        <GoogleAuthModal
          user={user}
          userProfile={userProfile}
          isOpen={showGoogleAuthModal}
          onClose={() => setShowGoogleAuthModal(false)}
          onUpdateProfile={(updated) => {
            setUserProfile(updated);
          }}
          onRedirectToProfile={() => handleSelectTab("perfil")}
          onToast={showToast}
        />

        {/* MODAL DE DOWNLOAD DO APP */}
        <AppDownloadModal
          isOpen={showDownloadAppModal}
          onClose={() => setShowDownloadAppModal(false)}
          onToast={showToast}
        />
      </div>
    </div>
  );
}

export default function App() {
  return (
    <AppProvider>
      <AuthProvider>
        <GameProvider>
          <AppContent />
        </GameProvider>
      </AuthProvider>
    </AppProvider>
  );
}
